/* eslint-disable */
import React, { useState, useRef, useCallback, useContext } from 'react';
import _ from 'underscore';
import { assertIsDefined } from '@bingads-webui-campaign-react/debug-assert';
import { IColumn, IDropdownOption } from '@fluentui/react';
import {WithTreeViewData, I18n } from '@bingads-webui-grid/well-known';
import { useDateRange } from '@bingads-webui-campaign-react/wizard-shared-components';
import { PanelSelectorProps, WithBulk } from './treeViewPanelSelector/PanelSelector';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { mergeData } from '@bingads-webui-campaign-react/product-services';
import { Model$ListingCondition, Model$ListingGroup } from './ApiType';
import { getValidListingConditions, getValidListingConditionsForWithoutPreselectedTreeNode } from './API/getListingGroupConditions';
import { useAPIsOfListingGroup } from './API';
import { ListingGroupGridItemData, TreeViewEventHandler } from './interface';
import { getAttributeTextOfConditionTemplate } from './render';
import { getAddAndDeleteItem, treeViewSaveWorker } from './treeViewPanelSelector/panel-callbacks';
import { buildTreeViewSelectorColumns, buildTreeViewSelectorItems, buildTreeViewSelectorItemsWithoutStatistics, buildTreeViewSelectorItemsByChildrenOfNode, fillDataToBulkAddedItem, ListingGroupTreeViewSelectorGridItem } from './treeViewPanelSelector/panel-helper';
import { ProductAttrubuteStatisticsResponse } from './API/productAttributeStatistics';
import { useLogger } from './logger';
import { getProductData, getProductScopeFromOData } from './API/getProductScope';
import { useAccountData } from './API/useAccountData';
import { offerLimitExceededWarningForListingGroup } from './render/utils';

/**
 * default value used in fluent-ui
 * reference https://github.com/microsoft/fluentui/blob/31345de30d356731eeb0ac1e658a2652f8f00aa7/packages/react/src/components/DetailsList/DetailsList.base.tsx
 */
interface UseTreeViewPanelSelectorProps {
  allRowRawData: Model$ListingGroup[] | undefined;
  setAllRowRawData: Function,
  scope: { save: Function, values: any };
  refreshGridData: Function;
  getArguemntsOfUpdateGridDataByCurrentState: Function,
  i18n: I18n;
  currentCampaign: any;
}

const NonSplittableConditions = ['CategoryL1', 'CategoryL2', 'CategoryL3', 'CategoryL4', 'CategoryL5'];

const DEFAULT_SALES_COUNTRY = 'US';

export const useTreeViewPanelSelector = (props: UseTreeViewPanelSelectorProps) => {
  const {
    i18n, allRowRawData, setAllRowRawData, scope, currentCampaign,
    refreshGridData,getArguemntsOfUpdateGridDataByCurrentState
  } = props;

const { productAttributeStatistics, getListingGroupConditions, updateListingTree } = useAPIsOfListingGroup();
const commonEntityPath = {
  accountId: scope.values.aid,
  campaignId: scope.values.campaignId || currentCampaign.Id,
  customerId: scope.values.cid,
  assetGroupId: scope.values.assetGroupId
};
const dataRange = useDateRange();
const logger = useLogger();
// #region TreeView
const [expandedItemIds, setExpandedItemIds] = useState(new Set<number>());
const [isTreeViewSelectorOpen, setIsTreeViewSelectorOpen] = useState(false);

const [treeViewSelectorDimensions, setTreeViewSelectorDimensions] = useState<IDropdownOption<any>[]>([]);
const [treeViewSelectorHeaderText, setTreeViewSelectorHeaderText] = useState('');
const [treeViewSelectorInitialSelectedItems, setTreeViewSelectorInitialSelectedItems] = useState<ListingGroupTreeViewSelectorGridItem[]>([]);
const [treeViewSelectorItems, setTreeViewSelectorItems] = useState<ListingGroupTreeViewSelectorGridItem[]>([]);
const [treeViewSelectorColumns, setTreeViewSelectorColumns] = useState<IColumn[]>([]);
const [isTreeViewShimmerEnabled, setIsTreeViewShimmerEnabled] = useState(false);
const serverContext = useContext(ServerContext);
const { getAccount } = useAccountData({ scope });
function onTreeViewCancel() {
  setIsTreeViewSelectorOpen(false);
  logger.signal('Cancel button is clicked in tree view selector');
}

const ADD_ONCE_LIMIT = 5_000;
const ADD_ONCE_LIMIT_STRING = "5,000";
const ALL_LIMIT = 20_000;
const ALL_LIMIT_STRING = "20,000";
const getErrorsForTreeViewSelector: PanelSelectorProps["getErrors"] = (items: WithBulk<ListingGroupTreeViewSelectorGridItem>[], selectedDimension: IDropdownOption<any>) => {
  assertIsDefined(currentEditTreeItem.current);
  assertIsDefined(allRowRawData);
  items.forEach(selectedItem => {
    fillDataToBulkAddedItem(selectedItem);
  });
  const { addedItems, deleteItemIds } = getAddAndDeleteItem(selectedDimension, items, currentEditTreeItem.current);
  const totalItemsCount = allRowRawData.length;
  const errors: string[] = [];
  if (addedItems.length + deleteItemIds.length > ADD_ONCE_LIMIT) {
    errors.push(i18n.getString(_TL_("Number of updated values should not exceed {{number}}."), { number: ADD_ONCE_LIMIT_STRING }));
  }
  if (totalItemsCount + addedItems.length - deleteItemIds.length > ALL_LIMIT) {
    errors.push(i18n.getString(_TL_("Change exceeds maximum allowed number of {{number}} Products in a single Asset Group."), { number: ALL_LIMIT_STRING }));
  }
  return errors;
}

const onGenericError = useCallback(() => {
  const alertHelper = new AlertHelper({ i18n });
  alertHelper.showError(i18n.getString(_TL_('We\'ve encountered an error and can\'t continue. Please contact support for further assistance.')));
}, [i18n]);

const showOfferLimitExceededError = useCallback(() => {
  const warning = offerLimitExceededWarningForListingGroup(i18n);
  const alertHelper = new AlertHelper({ i18n });
  alertHelper.showWarning(warning);
}, [i18n, scope]);

const onTreeViewSave: PanelSelectorProps["onSave"] = async (selectedItems: WithBulk<ListingGroupTreeViewSelectorGridItem>[], selectedDimension) => {
  logger.signal('Save button is clicked in tree view selector');
  assertIsDefined(currentEditTreeItem.current);
  assertIsDefined(scope.values.assetGroupId);
  assertIsDefined(scope.values.campaignId);
  selectedItems.forEach(selectedItem => {
    fillDataToBulkAddedItem(selectedItem);
  });
  await treeViewSaveWorker({
    selectedItems,
    selectedDimension,
    treeItem: currentEditTreeItem.current,
    assetGroupId: scope.values.assetGroupId,
    campaignId: scope.values.campaignId,
    callbackWhenSkipUpdate: () => {
      setIsTreeViewSelectorOpen(false);
    },
    callbackWhenUpdate: async (listingGroupsToAdd, listingGroupsToDelete) => {
      setIsTreeViewSelectorOpen(false);
      // change to loading state before sending update hotel listing tree API
      setAllRowRawData(undefined);

      await updateListingTree({
        ListingGroupsToAdd: listingGroupsToAdd,
        ListingGroupsToDelete: listingGroupsToDelete,
        AssetGroupId: scope.values.assetGroupId
      });

      await refreshGridData(getArguemntsOfUpdateGridDataByCurrentState());
    }
  });
}

// When updating tree view for Loactions, the 'Attribute' should be LocationID, but they are translated text when getting ListingGroup GridData.
// Tricky thing:
// If we want to add leaves under "Country", we also need to change the parent node(whose Operand is "Country") from "leaf" to "non-leaf" by deleting it and creating a new one.
// However, we do not know the LocationID of the parent node. So we have to send another request to get the mapping.
const currentSelectedDimensionGridDataTask = useRef<Promise<ProductAttrubuteStatisticsResponse>>();

const productAttributeStatisticsHelper = async (productListingGroupId: number, operand: string) => {
  return productAttributeStatistics({
    entityPath: {
      ...commonEntityPath,
      assetGroupId: scope.values.assetGroupId,
      productListingGroupId,
    },
    payload: {
      gridDataParameters: {
        DateRange: {
          StartDate: dataRange.StartDate,
          EndDate: dataRange.EndDate,
        }
      },
      operand,
    },
    queryOptions: {
      $top: 10000
    },
  })
}

async function updateTreeViewGridByEditTreeItemAndSelectedDimension(treeItem: WithTreeViewData<ListingGroupGridItemData>, selectedDimension: Model$ListingCondition) {
  assertIsDefined(selectedDimension?.Operand);
  setTreeViewSelectorColumns(buildTreeViewSelectorColumns(selectedDimension.Operand, selectedDimension.Operand, i18n, true));

  const task = productAttributeStatisticsHelper(treeItem.Id, selectedDimension.Operand);
  currentSelectedDimensionGridDataTask.current = task;
  const productAttrubuteStatisticsResponse = await task;
  const selectedDimensionGridData = productAttrubuteStatisticsResponse.value;

  const offerLimitExceeded = productAttrubuteStatisticsResponse.headers.get('Offerlimitexceeded');

  if (offerLimitExceeded === 'true') {
    showOfferLimitExceededError();
  }

  setTreeViewSelectorItems(buildTreeViewSelectorItems(
    selectedDimension,
    selectedDimensionGridData,
    treeItem.treeViewInfo.children));
}


function updateTreeViewGridByEditTreeItemAndSelectedDimensionWithoutStatistics(treeItem: WithTreeViewData<ListingGroupGridItemData>, selectedDimension: Model$ListingCondition) {
  assertIsDefined(selectedDimension?.Operand);
  setTreeViewSelectorColumns(buildTreeViewSelectorColumns(selectedDimension.Operand, selectedDimension.Operand, i18n, false));
  setTreeViewSelectorItems(buildTreeViewSelectorItemsWithoutStatistics(selectedDimension));
}

async function onTreeViewSelectedDimensionChange(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<any>, index?: number) {
  logger.signal('Change to another dimension in tree view selector');
  assertIsDefined(option);
  assertIsDefined(currentEditTreeItem.current);

  setIsTreeViewShimmerEnabled(true);

  const selectedDimension = currentValidListingConditions.current
    .find(c => c.Id === option.key);
  assertIsDefined(selectedDimension);

  await updateTreeViewGridByEditTreeItemAndSelectedDimension(currentEditTreeItem.current, selectedDimension);

  setIsTreeViewShimmerEnabled(false);
}

function onTreeViewSelectedDimensionChangeWithoutStatistics(event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<any>, index?: number) {
  logger.signal('Change to another dimension in tree view selector');
  assertIsDefined(option);
  assertIsDefined(currentEditTreeItem.current);

  const selectedDimension = currentValidListingConditions.current
    .find(c => c.Id === option.key);
  assertIsDefined(selectedDimension);

  updateTreeViewGridByEditTreeItemAndSelectedDimensionWithoutStatistics(currentEditTreeItem.current, selectedDimension);
}

// this data should be shared in different callbacks for one opened data
// use `Ref` rather than `State`, for this is useless for render
const currentValidListingConditions = useRef<Model$ListingCondition[]>([]);
// a quick and dirty way to share editing item
const currentEditTreeItem = useRef<WithTreeViewData<ListingGroupGridItemData>>();

function getInitialSelectedDimension(item: WithTreeViewData<ListingGroupGridItemData>, dimensions: Model$ListingCondition[]) {
  const DimensionProperties = [
    'Category', 'CategoryL1', 'CategoryL2', 'CategoryL3', 'CategoryL4', 'CategoryL5', 'Condition',
  ];
  let selectedDimensionName = '';

  if (item.treeViewInfo.children.length) {
    assertIsDefined(item.treeViewInfo.children[0].Condition?.Operand);
    selectedDimensionName = item.treeViewInfo.children[0].Condition.Operand;
  } else {
    const operands = dimensions.map(diemnsion => diemnsion.Operand);
    const findResult = DimensionProperties.find(dimension => operands.includes(dimension)) || operands[0];
    assertIsDefined(findResult);
    selectedDimensionName = findResult;
  }

  const result = dimensions.find(dimension => dimension.Operand === selectedDimensionName);
  assertIsDefined(result);
  return result;
}

async function getSupportedCountries() {
  const isOdataGetProductScopeEnabled = _.get(serverContext, ['DynamicPermissions', 'OdataGetProductScope'], false);
  const callStartTime = Date.now();
  const pData = isOdataGetProductScopeEnabled ?
    await getProductScopeFromOData(serverContext) :
    await getProductData({
      cid: scope.values.cid,
      aid: scope.values.aid,
      uid: scope.values.uid,
    });

  if (isOdataGetProductScopeEnabled) {
    logger.signal(`[PmaxListingGroup][OdataGetProductScopeForPmax] GetProductDataCallTime: ${Date.now() - callStartTime}`);
  } else {
    logger.signal(`[PmaxListingGroup] GetProductDataCallTime: ${Date.now() - callStartTime}`);
  }

  let data = pData;

  if (isOdataGetProductScopeEnabled && _.isArray(data)) {
      data = mergeData(pData);
  }

  const {
    SalesCountryCollection,
  } = data;
  if (!SalesCountryCollection) {
    return new Set();
  }
  return new Set(SalesCountryCollection.map(({ Value }) => Value.toUpperCase()));
}

// When FeedLabel is set then SalesCountry is empty, use supported account country, otherwise US
async function getCountryCode(shoppingSettings: any) {
  let countryCode = shoppingSettings?.SalesCountry;

  if (!countryCode) {
    const account = await getAccount();
    if (account && account.CountryCode) {
      const accountCountry = account.CountryCode;

      const supportedCountries = await getSupportedCountries();
      const ifAccountryCountrySupported = supportedCountries?.has(accountCountry);
      if (ifAccountryCountrySupported) {
        countryCode = accountCountry;
      }
    }
  }
  return countryCode || DEFAULT_SALES_COUNTRY;
}

const onTreeViewColumnAddOrEdit: TreeViewEventHandler = async (item) => {
  assertIsDefined(item);
  currentEditTreeItem.current = item;

  setIsTreeViewSelectorOpen(true);
  setIsTreeViewShimmerEnabled(true);
  setTreeViewSelectorHeaderText(getAttributeTextOfConditionTemplate(i18n, item));
  const shoppingSettings = currentCampaign?.CampaignSettings?.find(setting => setting['@odata.type'] === '#Model.ShoppingSettings');
  let countryCode = await getCountryCode(shoppingSettings);
  try {
    const getListingGroupConditionsResponseBody = await getListingGroupConditions({
      entityPath: commonEntityPath,
      queryOptions: {
        countryCode,
      },
    });
    assertIsDefined(item.ParentConditions);
    assertIsDefined(item.Condition);
    let validListingConditions = getValidListingConditions(
      getListingGroupConditionsResponseBody,
      [...item.ParentConditions, item.Condition],
      item);
    currentValidListingConditions.current = validListingConditions;

    const dimensionsOfItem = validListingConditions;
    const selectedDimension = getInitialSelectedDimension(item, dimensionsOfItem);
    const currentDimension =  getListingGroupConditionsResponseBody.value.find(dimension => dimension.Operand === item.Condition?.Operand);
    const currentAttributeId = currentDimension?.DomainData?.find(domainData => domainData.Value === item.Condition?.Attribute)?.Id;
    selectedDimension.DomainData = selectedDimension.DomainData?.filter(domainData => (!currentAttributeId && domainData.ParentId === 0) || domainData.ParentId === currentAttributeId);

    setTreeViewSelectorInitialSelectedItems(buildTreeViewSelectorItemsByChildrenOfNode(selectedDimension, item.treeViewInfo.children));

    setTreeViewSelectorDimensions(dimensionsOfItem.map<IDropdownOption>(dimension => {
      return {
        key: dimension.Id,
        text: i18n.getString(`ProductsGrid_Column_${dimension.Operand}`),
        selected: selectedDimension.Id === dimension.Id,
        data: {
          isBulkAddEnabled: !dimension.HasDomainData,
          operand: dimension.Operand,
        }
      }
    }));

    await updateTreeViewGridByEditTreeItemAndSelectedDimension(item, selectedDimension);
  } catch (e) {
    setIsTreeViewSelectorOpen(false);
    onGenericError();
    console.error(e);
  }
  setIsTreeViewShimmerEnabled(false);
}

const onFilterProduct: TreeViewEventHandler = async () => {
  setIsTreeViewSelectorOpen(true);
  setIsTreeViewShimmerEnabled(true);
  setTreeViewSelectorHeaderText(i18n.getString(_TL_('All Products')));
  const shoppingSettings = currentCampaign?.CampaignSettings[0];
  const countryCode = await getCountryCode(shoppingSettings);
  const getListingGroupConditionsResponseBody = await getListingGroupConditions({
    entityPath: commonEntityPath,
    queryOptions: {
      countryCode,
    },
  });

  const validListingConditions = getValidListingConditionsForWithoutPreselectedTreeNode(
    getListingGroupConditionsResponseBody,
    [{
      Attribute: '',
      Operand: 'All',
    }]);
  currentValidListingConditions.current = validListingConditions;

  const dimensionsOfItem = validListingConditions;
  const selectedDimension = dimensionsOfItem[0];

  setTreeViewSelectorDimensions(dimensionsOfItem.map<IDropdownOption>(dimension => {
    return {
      key: dimension.Id,
      text: i18n.getString(`ProductsGrid_Column_${dimension.Operand}`),
      selected: selectedDimension.Id === dimension.Id,
      data: {
        isBulkAddEnabled: !dimension.HasDomainData,
        operand: dimension.Operand,
      }
    }
  }));

  updateTreeViewGridByEditTreeItemAndSelectedDimensionWithoutStatistics({
    Id: 4578228980083101,
    treeViewInfo: {
      children: [],
      depth: 1,
      hasChildren: false,
      isExpanded: false,
      parent: undefined,
    },
    serverContext: undefined,
    scope,
    eventHandlers: {
      onTreeViewColumnAddOrEdit: undefined,
      onTreeViewColumnToggleCollapse: undefined
    },
    refreshGridData: _.identity,
    IsLeaf: false,
    IsExcluded: false,
    CampaignId: currentCampaign.Id,
    i18n: i18n
  }, selectedDimension);

  setIsTreeViewShimmerEnabled(false);
}
// #endregion TreeView
  return {
    expandedItemIds,
    setExpandedItemIds,
    isTreeViewSelectorOpen,
    setIsTreeViewSelectorOpen,
    treeViewSelectorColumns,
    treeViewSelectorDimensions,
    treeViewSelectorInitialSelectedItems,
    treeViewSelectorItems,
    treeViewSelectorHeaderText,
    onTreeViewCancel,
    onTreeViewSave,
    onTreeViewSelectedDimensionChange,
    onTreeViewSelectedDimensionChangeWithoutStatistics,
    isTreeViewShimmerEnabled,
    getErrorsForTreeViewSelector,
    onTreeViewColumnAddOrEdit,
    onFilterProduct,
  };
};
